/* eslint-disable complexity */
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type {
  CustomProductCommunity,
  FreeSignup
} from '@space-core/components/customCard/product/types'
import CustomImage from '@space-core/components/customImage'
import { ImageOrientation } from '@space-core/components/customImage/enum'
import HotIcon from '@space-core/components/icon'
import type { Variation } from '@space-core/components/tag'
import HotTag from '@space-core/components/tag'
import { useMediaQuery } from '@space-core/hooks'
import { ScreenSizes } from '@space-core/styles'
import { light } from '@space-core/styles/themes'
import { ThemeMode } from '@space-core/styles/themes/enum'
import clsx from 'clsx'
import styled, { css } from 'styled-components'

import { ICON_SIZES } from './constants'
import { TagColor, CardOrientation } from './enum'
import ThumbnailImage from './thumbnailImage'
import type { CardTag, IconSizeKey } from './types'

import '@hotmart/cosmos/dist/styles/utilities/text/variations/red.css'
import '@hotmart/cosmos/dist/styles/utilities/position.css'

type BaseImageContainerProps = {
  coverImage?: string
  avatar?: string
  avatarAltText?: string
  thumbnailImage?: string
  imageAlt?: string
  verticalCoverPage?: string
  verticalAltText?: string
  icon?: IconDefinition
  iconRounded?: boolean
  iconSize?: IconSizeKey
  tag?: CardTag
  containerOrientation?: CardOrientation
  imageOrientation?: ImageOrientation
  showDepthEffect?: boolean
  themeMode?: ThemeMode
  freeSignup?: FreeSignup
  stylesCustomProductCommunity?: CustomProductCommunity
}

const Icon = styled(HotIcon)`
  font-size: 3rem;
`

const ImageContainer = styled.div<{
  icon?: IconDefinition
  containerOrientation: CardOrientation
  stylesCustomProductCommunity: CustomProductCommunity | undefined
}>`
  ${({ icon }) => `background-color: ${icon ? 'var(--gray-200)' : 'var(--gray-100)'};`}
  ${({ containerOrientation, stylesCustomProductCommunity }) =>
    `
      aspect-ratio: ${
        containerOrientation === CardOrientation.HORIZONTAL
          ? '16 / 9'
          : stylesCustomProductCommunity?.aspectRatio || '9 / 13'
      };
    `}
`

const Tag = styled(HotTag)`
  bottom: 0;
  right: 0;
`

const IconWrapper = styled.div<{ isRounded: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  color: var(--gray-500);

  ${({ isRounded }) =>
    isRounded &&
    css`
      background: rgba(25, 28, 31, 0.2);
      color: ${light.colors.gray100} !important;

      ${Icon} {
        margin-left: 0.19rem;
      }
    `}
`

const TagFreeSignup = styled(Tag)<{
  verifyFreeSignUpIsMobileVertical?: boolean
}>`
  top: 0;
  left: 0;
  z-index: 1;
  width: fit-content;
  height: fit-content;
  ${({ verifyFreeSignUpIsMobileVertical }) =>
    verifyFreeSignUpIsMobileVertical
      ? `
      font-size: 10px;
    `
      : `
      font-size: 12px;
    `}
`
const BaseImageContainer = ({
  coverImage,
  avatar,
  avatarAltText,
  thumbnailImage,
  imageAlt,
  verticalCoverPage,
  verticalAltText,
  icon,
  iconSize = 'md',
  iconRounded = false,
  tag,
  containerOrientation = CardOrientation.HORIZONTAL,
  imageOrientation = ImageOrientation.HORIZONTAL,
  showDepthEffect = false,
  themeMode = ThemeMode.LIGHT,
  freeSignup,
  stylesCustomProductCommunity
}: BaseImageContainerProps) => {
  const tagClassName = clsx(
    '_text-1 _text-gray-900 _position-absolute _border-0 _rounded _mr-2 _mb-2',
    {
      '_text-red': tag?.color === TagColor.RED
    }
  )

  const imageClassName = clsx('_rounded-lg', {
    '_position-relative': icon || tag,
    '_d-flex _justify-content-center _align-items-center _h-full': thumbnailImage
  })

  const iconWrapperClasses = clsx(' _d-flex _align-items-center _justify-content-center', {
    '_rounded-circle': iconRounded
  })

  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.sm})`)

  const verifyFreeSignUpIsMobileVertical =
    isMobile && containerOrientation === CardOrientation.VERTICAL && freeSignup?.isFreeSignup

  const freeSignupTagClass = clsx('_border-0 _position-absolute', {
    '_ml-2 _mt-2': !verifyFreeSignUpIsMobileVertical,
    '_ml-1 _mt-1': verifyFreeSignUpIsMobileVertical
  })
  const imageValue =
    coverImage && containerOrientation === CardOrientation.HORIZONTAL
      ? coverImage
      : verticalCoverPage && containerOrientation === CardOrientation.VERTICAL
      ? verticalCoverPage
      : false

  const isCoverImageHorizontal = coverImage && containerOrientation === CardOrientation.HORIZONTAL

  const thumbnailImageValue = avatar ? avatar : thumbnailImage

  const imageAltValue = avatarAltText ? avatarAltText : imageAlt

  return (
    <>
      <ImageContainer
        className={imageClassName}
        icon={icon}
        containerOrientation={containerOrientation}
        stylesCustomProductCommunity={stylesCustomProductCommunity}>
        {freeSignup?.isFreeSignup && (
          <TagFreeSignup
            className={freeSignupTagClass}
            variation={freeSignup.color as Variation}
            role='term'
            verifyFreeSignUpIsMobileVertical={verifyFreeSignUpIsMobileVertical}>
            {freeSignup.text}
          </TagFreeSignup>
        )}

        {icon && (
          <div
            className='_d-flex _position-absolute _align-items-center _justify-content-center _w-full _h-full'
            data-test='card-icon'>
            <IconWrapper
              className={iconWrapperClasses}
              isRounded={iconRounded}
              data-test='card-icon-wrapper'>
              <Icon
                data-test='card-icon-svg'
                icon={icon}
                width={ICON_SIZES[iconSize].width}
                height={ICON_SIZES[iconSize].height}
              />
            </IconWrapper>
          </div>
        )}

        {tag && (
          <Tag className={tagClassName} data-test='card-tag' role='term'>
            {tag.icon && <HotIcon icon={tag.icon} className='_mr-2' data-test='tag-icon' />}
            <b>{tag.text}</b>
          </Tag>
        )}

        {imageValue && (
          <CustomImage
            className='_rounded-lg'
            src={isCoverImageHorizontal ? coverImage : verticalCoverPage}
            alt={isCoverImageHorizontal ? imageAlt : verticalAltText}
            fillImage
            orientation={imageOrientation}
          />
        )}

        {!imageValue && thumbnailImageValue && (
          <ThumbnailImage
            thumbnailImage={thumbnailImageValue}
            imageAlt={imageAltValue}
            showDepthEffect={showDepthEffect}
            themeMode={themeMode}
            thumbOrientatiion={containerOrientation}
          />
        )}
      </ImageContainer>
    </>
  )
}

export default BaseImageContainer
